import React, { useState, useEffect, useCallback } from 'react';
import { RekeyUnit, texasComplianceService } from '../../services/texasComplianceService';

export interface RekeyStatusWidgetProps {
  onUnitClick?: (unit: RekeyUnit) => void;
  onPropertyClick?: (propertyId: string) => void;
  initialData?: RekeyUnit[];
  propertyManagerFilter?: string;
}

export const RekeyStatusWidget: React.FC<RekeyStatusWidgetProps> = ({
  onUnitClick,
  onPropertyClick,
  initialData,
  propertyManagerFilter: externalPmFilter,
}) => {
  const [units, setUnits] = useState<RekeyUnit[]>(initialData || []);
  const [loading, setLoading] = useState<boolean>(!initialData);
  const [selectedPm, setSelectedPm] = useState<string>(externalPmFilter || 'all');
  const [error, setError] = useState<string | null>(null);

  const fetchUnits = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await texasComplianceService.getRekeyStatus(selectedPm);
      setUnits(data);
    } catch (err: any) {
      setError(err?.message || 'Failed to load rekey turnover units');
    } finally {
      setLoading(false);
    }
  }, [selectedPm]);

  useEffect(() => {
    if (!initialData || selectedPm !== 'all') {
      fetchUnits();
    }
  }, [fetchUnits, initialData, selectedPm]);

  // Extract unique Property Managers for filter
  const propertyManagers = Array.from(
    new Set(units.map((u) => u.propertyManagerName).filter(Boolean))
  );

  const filteredUnits = selectedPm === 'all'
    ? units
    : units.filter((u) => u.propertyManagerName === selectedPm || u.propertyManagerId === selectedPm);

  const getStatusBadge = (item: RekeyUnit) => {
    if (item.completed) {
      return (
        <span
          data-testid="rekey-badge-compliant"
          className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700 border border-emerald-200"
        >
          <i className="fas fa-check mr-1"></i>Rekeyed
        </span>
      );
    }

    if (item.status === 'overdue') {
      return (
        <span
          data-testid="rekey-badge-overdue"
          className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-700 border border-red-200 uppercase"
        >
          <i className="fas fa-triangle-exclamation mr-1"></i>
          {Math.abs(item.daysRemaining) + 'd Overdue'}
        </span>
      );
    }

    if (item.status === 'due_soon') {
      return (
        <span
          data-testid="rekey-badge-due-soon"
          className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-200"
        >
          <i className="fas fa-clock mr-1"></i>
          {item.daysRemaining + 'd Left (Due Soon)'}
        </span>
      );
    }

    return (
      <span
        data-testid="rekey-badge-compliant"
        className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700 border border-emerald-200"
      >
        {item.daysRemaining + 'd Left (Compliant)'}
      </span>
    );
  };

  return (
    <div
      data-testid="rekey-status-widget"
      className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden p-4"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3 border-b border-gray-100 pb-2.5">
        <div className="flex items-center space-x-2.5">
          <div className="w-8 h-8 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center font-bold text-sm">
            <i className="fas fa-key"></i>
          </div>
          <div>
            <h3 className="font-bold text-gray-900 text-sm flex items-center gap-2">
              7-Day Rekey Status
              <span className="text-[10px] font-medium px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded border border-blue-200">
                Texas § 92.156
              </span>
            </h3>
            <p className="text-[11px] text-gray-500">
              Mandatory rekeying within 7 days of tenant turnover
            </p>
          </div>
        </div>

        {/* Filter by Property Manager */}
        {propertyManagers.length > 0 && (
          <select
            data-testid="pm-filter-select"
            value={selectedPm}
            onChange={(e) => setSelectedPm(e.target.value)}
            className="text-xs border border-gray-300 rounded-md px-2 py-1 bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-amber-500"
          >
            <option value="all">All Managers</option>
            {propertyManagers.map((pm) => (
              <option key={pm as string} value={pm as string}>
                {pm}
              </option>
            ))}
          </select>
        )}
      </div>

      {error && (
        <div className="p-2 mb-2 bg-red-50 border border-red-200 rounded text-xs text-red-600">
          {error}
        </div>
      )}

      {loading && units.length === 0 ? (
        <div className="py-8 text-center text-gray-400 text-xs">
          <i className="fas fa-spinner fa-spin mr-2"></i>Loading rekey turnover units...
        </div>
      ) : filteredUnits.length === 0 ? (
        <div className="py-8 text-center text-gray-400 text-xs">
          <i className="fas fa-check-double text-emerald-500 text-lg mb-1 block"></i>
          All turnover units are 100% compliant with rekeying law.
        </div>
      ) : (
        <div className="space-y-2.5">
          {filteredUnits.map((unit) => (
            <div
              key={unit.id}
              data-testid={'rekey-unit-' + unit.id}
              onClick={() => onUnitClick && onUnitClick(unit)}
              className="p-3 bg-gray-50 hover:bg-gray-100/80 rounded-lg border border-gray-200/80 cursor-pointer transition-all flex items-center justify-between"
            >
              <div className="flex-1 min-w-0 pr-3">
                <div className="flex items-center space-x-2 mb-0.5">
                  <span className="font-bold text-gray-900 text-xs">
                    Unit {unit.unitNumber}
                  </span>
                  <span
                    onClick={(e) => {
                      if (onPropertyClick) {
                        e.stopPropagation();
                        onPropertyClick(unit.propertyId);
                      }
                    }}
                    className="text-[11px] text-blue-600 hover:underline font-medium truncate"
                  >
                    {unit.propertyName}
                  </span>
                </div>

                <div className="flex items-center space-x-3 text-[11px] text-gray-500">
                  <span>Turnover: {unit.turnoverDate}</span>
                  <span>Deadline: {unit.rekeyDeadline}</span>
                  {unit.propertyManagerName && (
                    <span className="text-gray-400">PM: {unit.propertyManagerName}</span>
                  )}
                </div>
              </div>

              <div className="flex-shrink-0">
                {getStatusBadge(unit)}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Footer stats */}
      <div className="mt-3 pt-2 border-t border-gray-100 flex items-center justify-between text-[10px] text-gray-400">
        <span>Texas Property Code Ch. 92 Subchapter D</span>
        <span>
          {filteredUnits.filter((u) => u.status === 'overdue').length + ' overdue • ' +
           filteredUnits.filter((u) => u.status === 'due_soon').length + ' due soon'}
        </span>
      </div>
    </div>
  );
};

export default RekeyStatusWidget;
